import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { complaintAPI } from '../../services/api';
import { MoreHorizontal, X, LogOut, ArrowLeft, ArrowLeftRight } from 'lucide-react';
import NotificationBell from './TechnicalNotificationBell';
import ProfileMenu from '../common/ProfileMenu';
import { bottomTabs, moreItems, getPageTitle } from './technicalNavConfig';
import { useCompanyIdentity } from '../../hooks/useCompanyIdentity';
import { IS_LAXMI_HOST, DEMO_BRAND } from '../../utils/demo';
import hasRole from '../../utils/hasRole';
import useAppearance from '../../theme/useAppearance';

const isTabActive = (pathname, to) => pathname === to || pathname.startsWith(to + '/');

// Slightly cooler than the admin --bg (#f8fafc). The whole shell is white cards on this
// tint, and at 15px text a neutral grey leaves the cards barely separated from the page —
// a trace of blue in the background is what makes a white card read as raised.
const APP_BG = 'var(--cb-f4f6fc)';

// Bottom-nav "app view" for the technical login — mobile-first counterpart to the sidebar
// TechnicalLayout, same toggle pattern as AdminAppLayout/AdminLayout (useViewMode). Deliberately
// lighter than AdminAppLayout: no customer search, no admin-configurable tabs, no billing
// floating buttons — technical has none of that surface.
export default function TechnicalAppLayout() {
  const { user, logout } = useAuth();
  useAppearance();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [openComplaints, setOpenComplaints] = useState(0);
  // Technical team is the broadband/network side of the business — Angel Internet
  // Services' own identity, not catv's Laxmi Cable Vision (used by the agent app).
  const brand = useCompanyIdentity('broadband');

  useEffect(() => {
    complaintAPI.getAll({ status: 'open' }).then(({ data }) => setOpenComplaints(data.data.length)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const isHome = isTabActive(location.pathname, '/technical/dashboard');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: APP_BG }}>
      {/* Branded, left-aligned masthead rather than a centred page title: this shell is the
          technician's whole app, not a stack of pages inside a bigger product, so the top
          line names the company and the second line says where you are. On the home screen
          there is no page to name, so it says what the app is instead.

          No bottom border — the header shares the page's tint and the white cards below
          provide the edge. A rule across the top of a phone screen only steals height. */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 60, background: APP_BG, flexShrink: 0,
        height: 'calc(62px + env(safe-area-inset-top, 0px))',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        display: 'flex', alignItems: 'center',
        paddingLeft: 12, paddingRight: 12, gap: 10,
      }}>
        {/* The logo file is the full lockup (mark + wordmark), so it is used here as a mark
            only — at 34px the wordmark inside it is texture, and the readable name is the
            HTML text beside it. */}
        <img src={brand.logo || (IS_LAXMI_HOST ? '/LCV.png?v=1' : DEMO_BRAND.logo)} alt="" style={{ width: 51, height: 51, objectFit: 'contain', flexShrink: 0 }} />
        {user?.role !== 'technical' && (
          <button
            onClick={() => navigate('/dashboard')}
            title="Back to Admin Portal"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, flexShrink: 0, borderRadius: 9,
              background: 'var(--cb-ffffff)', border: '1px solid var(--border)', color: 'var(--text)', cursor: 'pointer',
            }}
          >
            <ArrowLeft size={15} />
          </button>
        )}
        {hasRole(user, 'agent') && (
          <button
            onClick={() => navigate('/agent')}
            title="Switch to Agent App"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, flexShrink: 0, borderRadius: 9,
              background: 'var(--cb-ffffff)', border: '1px solid var(--border)', color: 'var(--text)', cursor: 'pointer',
            }}
          >
            <ArrowLeftRight size={15} />
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 800, fontSize: '1rem', letterSpacing: '-.01em', color: 'var(--c-111827)', lineHeight: 1.2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {brand.name || (IS_LAXMI_HOST ? 'Angel Internet Services' : DEMO_BRAND.name)}
          </div>
          <div style={{
            fontSize: '.73rem', color: 'var(--text-muted)', lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {isHome ? 'Network Operations' : getPageTitle(location.pathname)}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <NotificationBell />
          <ProfileMenu user={user} onLogout={handleLogout} size={34} />
        </div>
      </header>

      <main style={{ flex: 1, overflowY: 'auto', paddingBottom: 12 }}>
        <div className="page-content" style={{ padding: 14 }}>
          <Outlet />
        </div>
      </main>

      {/* Raised bar rather than a bordered strip: rounded top corners and a shadow that
          points upward, so the nav reads as sitting on top of the page the same way the
          cards do. The active tab is marked twice — colour plus the underline — because
          colour alone is the one cue a technician squinting at a phone in daylight loses. */}
      <nav style={{
        position: 'sticky', bottom: 0, background: 'var(--cb-ffffff)',
        borderRadius: '20px 20px 0 0', boxShadow: '0 -4px 20px -8px rgba(16,24,40,.18)',
        display: 'flex', flexShrink: 0, zIndex: 60,
        height: 'calc(62px + env(safe-area-inset-bottom, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        {[
          ...bottomTabs.map(tab => ({
            key: tab.id, icon: tab.icon, label: tab.label,
            active: isTabActive(location.pathname, tab.to),
            onClick: () => navigate(tab.to),
          })),
          {
            key: 'more', icon: MoreHorizontal, label: 'More',
            active: moreOpen, onClick: () => setMoreOpen(true),
          },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={tab.onClick}
            style={{
              flex: 1, position: 'relative',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 3, background: 'none', border: 'none', cursor: 'pointer',
              color: tab.active ? 'var(--primary)' : 'var(--text-muted)',
              fontSize: '.68rem', fontWeight: tab.active ? 700 : 600,
            }}
          >
            <tab.icon size={21} strokeWidth={tab.active ? 2.4 : 2} />
            {tab.label}
            {tab.active && (
              <span style={{
                position: 'absolute', bottom: 4, width: 30, height: 3, borderRadius: 2,
                background: 'var(--primary)',
              }} />
            )}
          </button>
        ))}
      </nav>

      {moreOpen && (
        <div onClick={() => setMoreOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 100 }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, maxHeight: '80vh', overflowY: 'auto',
              background: 'var(--bg-card, #fff)', borderRadius: '16px 16px 0 0', padding: '4px 0 24px',
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 16px 12px', borderBottom: '1px solid var(--border)',
            }}>
              <span style={{ fontWeight: 700, fontSize: '.95rem' }}>Menu</span>
              <button onClick={() => setMoreOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={20} />
              </button>
            </div>

            {moreItems.map(item => (
              <button
                key={item.id}
                onClick={() => { navigate(item.to); setMoreOpen(false); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: '.88rem',
                  color: 'inherit', textAlign: 'left',
                }}
              >
                <item.icon size={17} />
                {item.label}
                {item.badgeKey === 'complaints' && openComplaints > 0 && (
                  <span className="badge" style={{ marginLeft: 'auto' }}>{openComplaints}</span>
                )}
              </button>
            ))}

            <button
              onClick={handleLogout}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', marginTop: 8,
                background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer',
                fontSize: '.88rem', color: 'var(--danger, #dc2626)', textAlign: 'left',
              }}
            >
              <LogOut size={17} /> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
